import { t } from "i18next";
import _, { isUndefined } from "lodash";
import { DateTime, Duration } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useFetch } from "../../../../hooks/useFetch";
import { calculateSummaryPersonUsage } from "../../../../lib/calculations";
import { useAppStore } from "../../../../stores/RootStore";
import { Event, InteractionFromEvents, Person, SelectOption, UsageDatasetByPersonnels } from "../../../../types/types";
import { Button } from "../../../../ui/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "../../../../ui/DropdownMenu/DropdownMenu";
import { MonthPicker } from "../../../../ui/MonthPicker/MonthPicker";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import Spinner from "../../../../ui/Spinner/Spinner";
import { EquipmentInteractions } from "./blocks/EquipmentInteractions";
import { LoadBarChart } from "./blocks/LoadBarChart";
import { PersonLoaded } from "./blocks/PersonLoaded";

interface DateTimeRange {
  from: DateTime,
  to: DateTime
}

interface DayData {
  day: string;
  totalDayWorked: number;
  totalDayStopped: number;
  totalDayInteraction: number;
  shiftDuration: number;
  equipments: number[];
  interactions: InteractionFromEvents[];
  eventsDayData: Event[];
}

export function MonthReport({person}:{person: Person}) {
  const {shifts, equipments} = useAppStore()
  const shiftsList = shifts.map(s => ({label: s.name, value: s.id})) as SelectOption[]
  const [selectedDateRange, setSelectedDateRange] = useState<DateTimeRange>({
    from: DateTime.local().startOf('month'),
    to: DateTime.local().endOf('month')
  })
  const [currentShift, setCurrentShift] = useState<SelectOption>(shiftsList.find(s => s.value === "full")!)
  const [loadingMonth, setLoadingMonth] = useState<boolean>(true)
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(true)
  const [monthParams, setMonthParams] = useState(new URLSearchParams({}));
  const [monthData, setMonthData] = useState<UsageDatasetByPersonnels[] | null>(null)
  const [by, setBy] = useState<"equipment" | "days" | string>("equipment")
  const [{ response: monthResponse, isLoading: monthLoading, error: monthError }, fetchMonthData] =
  useFetch(`/directories/ng_equipment_item_events/usage_dataset_by_personnels?${monthParams.toString()}`);

  useEffect(() => {
    setDataIsLoading(true)
    setMonthData(null)
    setLoadingMonth(true)
    setParamsForMonth()
    fetchMonthData()
  }, [])

  useEffect(() => {
    if(!selectedDateRange || !currentShift) { return }
    setDataIsLoading(true)
    setMonthData(null)
    setLoadingMonth(true)
    setParamsForMonth()
    fetchMonthData()
  },[currentShift]) 

  useEffect(() => {
    if(!selectedDateRange || !currentShift) { return }

    setDataIsLoading(true)
    setMonthData(null)
    setLoadingMonth(true)
    setParamsForMonth()
    fetchMonthData()
  }, [selectedDateRange])

  function setParamsForMonth() {
    let newMonthParams = new URLSearchParams({});
    let responseShift = shifts.find(s => s.id == currentShift!.value)!

    let endOfShiftResponse = selectedDateRange.from.set({hour: responseShift.end_at.hour, minute: responseShift.end_at.minute, second: responseShift.end_at.second})
    
    newMonthParams.append("personnel_ids[]", person.id)
    newMonthParams.append("from", selectedDateRange.from.startOf('month').toSQL()!);
    newMonthParams.append("to", selectedDateRange.to.endOf('month').toSQL()!);
    newMonthParams.append("day_time_from", responseShift?.start_at.toFormat("HH:mm:ss")!);
    if(endOfShiftResponse > DateTime.local()) {
      newMonthParams.append("day_time_to", responseShift?.end_at.toFormat("HH:mm:ss")!);
    } else {
      newMonthParams.append("day_time_to", responseShift?.end_at.toFormat("HH:mm:ss")!);
    }
    setMonthParams(newMonthParams)
  }

  useEffect(() => {
    if(!monthResponse) { return }
    setMonthData(_.orderBy(monthResponse.data, "at") as unknown as UsageDatasetByPersonnels[])
    setLoadingMonth(false)
    setDataIsLoading(false)
  }, [monthResponse])

  const reportData = useMemo(() => {return calculateSummaryPersonUsage(monthData!, shifts.find(s => s.id === currentShift.value)!, equipments!, person)}, [monthData])

  function handleChangeDate(value: DateTimeRange) {
    setDateWithChecking(value!)
  }

  const setDateWithChecking = (value: DateTimeRange | any) => {
    if (isUndefined(value)) {
      setSelectedDateRange({
        from: selectedDateRange.from.startOf('month'),
        to: selectedDateRange.from.endOf('month')
      });
      return;
    }
    if (isUndefined(value.from))
      setSelectedDateRange({
      from: DateTime.local().startOf("month"),
      to: DateTime.local().endOf('month'),
      });
    if (isUndefined(value.to))
      setSelectedDateRange({
        from: value.startOf("month"),
        to: value.endOf('month'),
      });
    else setSelectedDateRange(value);
  };

  // CSV
  const csvData = [
    [
      t("at"),
      t("weekday"),
      t("shift"),
      t("shiftDuration"),
      t("interaction"),
      t('start'),
      t('start_work'),
      t('finish'),
      t('finish_work'),
      
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
      t("interaction"),
    ],
    ...reportData.data!.map((day) => {
      return [
        day.dateShift.start_day.toFormat("dd.MM.yyyy"),
        day.dateShift.start_day.weekdayLong,
        day.dateShift.label,
        Duration.fromMillis(day.dateShift.duration * 1000).toFormat("hh:mm:ss"),
        day.interactions.map(i => i.equipment.attributes.name).join(", "),
        day.start.toFormat("dd.MM.yyyy HH:mm:ss"),
        day.startWork.toFormat("dd.MM.yyyy HH:mm:ss"),
        day.end.toFormat("dd.MM.yyyy HH:mm:ss"),
        day.endWork.toFormat("dd.MM.yyyy HH:mm:ss"),
        day.interaction.total.seconds,
        day.interaction.total.percent,
        day.interaction.total.time,
        day.interaction.on.seconds,
        day.interaction.on.percent,
        day.interaction.on.time,
        day.interaction.worked.seconds,
        day.interaction.worked.percent,
        day.interaction.worked.time,
        day.interaction.stopped.seconds,
        day.interaction.stopped.percent,
        day.interaction.stopped.time,
        day.interaction.off.seconds,
        day.interaction.off.percent,
        day.interaction.off.time
      ]
    }),
  ];


  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <div className="w-full">
        {selectedDateRange && currentShift && <div className={`flex flex-none justify-between py-2 px-6 border-b ${dataIsLoading ? "pointer-events-none" : ""}`}>
          <div className="flex items-center">
            <span className="text-sm">{t('report_by_period')}</span>
            <MonthPicker 
              selected={selectedDateRange}
              onSelect={handleChangeDate}
              variant={"link"}
              algin={"start"}
              classname="text-primary"
            />
            <span className="text-sm">{t('splitted_by')}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant={"link"} size={"datepicker"} className="lowercase">{by === "equipment" ? t("by_equipment") : t("by_days")}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[200px]">
                <DropdownMenuRadioGroup value={by} onValueChange={(value) => setBy(value)}>
                  <DropdownMenuRadioItem key={"equipment"} value={"equipment"}>{t('by_equipment')}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem key={"days"} value={"days"}>{t('by_days')}</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="space-x-2">
            {dataIsLoading && monthData && monthData.length === 0 ? <></> : <CSVLink data={csvData} filename={`${person.attributes.last_name}_${selectedDateRange.from.monthLong}.csv`} separator={";"}>
              <Button variant="outline" size="datepicker">{t('export')}</Button>
            </CSVLink>}
          </div>
        </div>}
      </div>
      {loadingMonth ? <div className="flex h-full w-full"><Spinner/></div> : 
        monthData && monthData.length === 0 ? <div className="flex w-full h-full items-center justify-center px-6">{t('no_data_for_this_period')}</div> :
        <ScrollArea>
          <div>
            <div className="px-6">
              <PersonLoaded person={person} data={reportData}/>
            </div>
            
            <div className="px-6 space-y-2">
              <LoadBarChart data={reportData} by={by}/>
            </div>

            <div className="px-6 h-full">
              <EquipmentInteractions person={person} data={reportData} by={by}/>
            </div>
          </div>
        </ScrollArea>
      }
    </div>
  )
}
